import {
  ExpressCheckoutElement,
  ExpressCheckoutElementProps,
  useElements,
} from "@stripe/react-stripe-js";
import {
  Box,
  Text,
  LinkText,
  VStack,
  Stack,
  PrimaryButton,
  Checkbox,
} from "flicket-ui";
import React, { FormEvent, useEffect, useState } from "react";
import Modal from "~components/common/Modal";
import { checkoutDebug } from "~lib/debug";
import Skeleton from "react-loading-skeleton";
import { CheckoutProvider, useCheckoutContext } from "~context/checkoutContext";
import { useRouter } from "next/router";
import { removeQueryParams } from "~lib/helpers/queryParams";
import AnimatedLoadingSpinner from "~features/checkout/common/components/AnimatedLoadingSpinner/AnimatedLoadingSpinner";
import {
  AvailablePaymentMethods,
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";
import { useExpressCheckoutContext } from "./ExpressCheckout.context";
import { useSDK } from "~hooks";
import { OrderStatus, OrderQuery, VenueSeating } from "~graphql/sdk";
import { FormikProvider } from "formik";
import { DeliveryBlockWithQueue } from "~features/checkout/common/components/DeliveryBlockWithQueue";
import { TicketCoverWithQueue } from "~features/checkout/common/components/TicketCoverWithQueue";
import { useExpressCheckoutForm } from "../hooks/useExpressCheckoutForm";
import { Timer } from "~components/checkout/checkout.Timer";
import { Icon } from "~components";
import { Timer as PhosphorTimer } from "@phosphor-icons/react";
import { shouldShowTicketCover } from "~features/checkout/common/utils/ticketCover.util";
import { useModal } from "~components/common/Modal/useModal";
import { OrderSummary } from "./OrderSummary/OrderSummary";
import { OrderSummaryProvider } from "./OrderSummary/OrderSummaryProvider";
import { AnimatePresence, motion } from "framer-motion";
import { showToast } from "~lib";
import { constructTermsAndConditionsUrlFromPath } from "~lib/helpers/constructTermsAndConditionsUrl";
import { captureException } from "@sentry/nextjs";

export interface ExpressCheckoutModalContentProps {
  isLoading: boolean;
}

export function ExpressCheckoutModal() {
  const router = useRouter();
  const sdk = useSDK();

  return (
    <Modal
      alignModalTop={true}
      darkBackdrop={true}
      isOpen={router.query.modal === "express-checkout"}
      close={() => {
        if (router.query.orderId) {
          try {
            // Silently expire order
            void sdk.expireOrder({
              id: router.query.orderId as string,
            });
          } catch (error) {
            // Do nothing
          }
        }
        removeQueryParams(router, ["orderId", "paymentType", "modal"]);
      }}
      dialogContentProps={{
        width: "600px",
      }}
      showCloseButton={false}
    >
      {!router.query.orderId && <LoaderContent message="Securing tickets" />}
      {router.query.orderId && (
        <ExpressCheckoutModalContent isLoading={!router.query.orderId} />
      )}
    </Modal>
  );
}
export function ExpressCheckoutModalContent(
  props: ExpressCheckoutModalContentProps
) {
  return (
    <CheckoutProvider>
      <ExpressCheckoutModalOrderLoader {...props} />
    </CheckoutProvider>
  );
}

function ExpressCheckoutModalOrderLoader(
  props: ExpressCheckoutModalContentProps
) {
  const router = useRouter();
  const { order, isLoading, error } = useCheckoutContext();
  const { isLoadingExpressCheckoutElement } = useExpressCheckoutContext();
  const { close } = useModal();

  if (!router.query.orderId || isLoading || isLoadingExpressCheckoutElement) {
    return <LoaderContent />;
  }

  if (error) {
    return (
      <React.Fragment key="error">
        <Modal.Header>Unexpected error</Modal.Header>
        <Modal.Content>
          There was an unexpected error creating your order. Please try again.
        </Modal.Content>
        <Modal.Footer>
          <PrimaryButton onClick={close}>Close</PrimaryButton>
        </Modal.Footer>
      </React.Fragment>
    );
  }

  // Explicit workflow for expired orders
  if (order?.status === OrderStatus.Expired) {
    return (
      <React.Fragment key="expired">
        <Modal.Header>Order expired</Modal.Header>
        <Modal.Content>
          This order has expired. Please go back and try again.
        </Modal.Content>
        <Modal.Footer>
          <PrimaryButton onClick={close}>Close</PrimaryButton>
        </Modal.Footer>
      </React.Fragment>
    );
  }

  // For everything else that isn't pending, just show a generic error
  if (order?.status !== OrderStatus.Pending) {
    return (
      <React.Fragment key="pending">
        <Modal.Header>Invalid order</Modal.Header>
        <Modal.Content>Please go back and try again.</Modal.Content>
        <Modal.Footer>
          <PrimaryButton onClick={close}>Close</PrimaryButton>
        </Modal.Footer>
      </React.Fragment>
    );
  }

  return (
    <OrderSummaryProvider>
      <ExpressCheckoutModalContentForm {...props} />
    </OrderSummaryProvider>
  );
}

type Query = {
  paymentType: keyof AvailablePaymentMethods;
  orderId: string;
  modal: "express-checkout";
};

function ExpressCheckoutModalContentForm(
  props: ExpressCheckoutModalContentProps
) {
  const router = useRouter();
  const query = router.query as Query;

  const elements = useElements();
  const { formik, handleConfirm, paymentInProgress } = useExpressCheckoutForm();

  const { order, isLoading: isOrderUpdating, totals } = useCheckoutContext();
  const showTicketCover = shouldShowTicketCover(order);

  const title = order?.event?.title || order?.membership?.name;

  useEffect(() => {
    if ((totals?.remainingPayment >= 0, elements)) {
      elements.update({ amount: Math.round(totals.remainingPayment * 100) });
    }
  }, [totals, elements]);

  function handleElementLoadError(e) {
    console.error("Strip Express Checkout element failed to load", e);

    // Close the modal and display and error
    removeQueryParams(router, ["modal", "paymentType", "orderId"]);

    showToast("Unable to load payment methods. Please try again", "error");

    captureException(
      new Error("Error loading stripe express checkout element"),
      {
        tags: {
          checkoutType: "express",
        },
        extra: {
          error: e,
        },
      }
    );
  }

  const showHiddenSubmit =
    !formik.isValid ||
    (showTicketCover && formik.values?.ticketCover === undefined);

  return (
    <>
      {/* Payment progress is an overly because the Stripe element needs to be
    mounted while payment is being confirmed */}
      {paymentInProgress && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={10}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
          bg={"rgba(255, 255, 255, 0.95)" as any}
        >
          <LoaderContent message="Payment in progress..." />
        </Box>
      )}

      <FormikProvider value={formik}>
        <Modal.Header>
          Complete your order
          {!paymentInProgress && <ExpressTimer order={order} />}
        </Modal.Header>
        <Modal.Content>
          <AnimateFadeIn>
            <VStack gap={4}>
              <DeliveryBlockWithQueue />

              {showTicketCover && <TicketCoverWithQueue />}

              <VStack gap={1}>
                <Text variant="header.S">Stay in the loop</Text>
                <Checkbox
                  variant="card"
                  label={`Receive updates, news, and special offers from ${title} via email and text messages.`}
                  checked={formik.values.usableForMarketing}
                  onChange={(e) => {
                    void formik.setFieldValue(
                      "usableForMarketing",
                      e.target.checked
                    );
                  }}
                />
              </VStack>
              <VStack gap="6/4">
                <VStack gap={2}>
                  <OrderSummary />

                  <Text variant="small">
                    By completing this purchase you agree to the{" "}
                    <LinkText
                      target="_blank"
                      href={constructTermsAndConditionsUrlFromPath(
                        router.asPath
                      )}
                    >
                      Event Terms and Conditions
                    </LinkText>{" "}
                    and{" "}
                    <LinkText target="_blank" href="/privacy-policy">
                      Privacy Policy
                    </LinkText>
                    .
                  </Text>
                  <ExpressCheckoutElementAndWrapper
                    hiddenSubmitClick={() => {
                      void formik.submitForm();
                    }}
                    paymentType={query.paymentType}
                    isOrderUpdating={isOrderUpdating}
                    showHiddenSubmit={showHiddenSubmit}
                    handleElementLoadError={handleElementLoadError}
                    handleConfirm={handleConfirm}
                  />
                </VStack>
              </VStack>
            </VStack>
          </AnimateFadeIn>
        </Modal.Content>
      </FormikProvider>
    </>
  );
}

function LoaderContent({ message = "" }: { message?: string }) {
  return (
    <Modal.Content>
      <Stack
        direction="vertical"
        gap={1}
        alignItems="center"
        justifyContent="center"
        height="386px"
        padding={1}
      >
        <Text variant="header.M" textAlign="center">
          {message}
        </Text>
        <AnimatedLoadingSpinner loading={true} onComplete={console.log} />
      </Stack>
    </Modal.Content>
  );
}

const TWO_MINUTES = 60 * 2;

function ExpressTimer({ order }: { order?: OrderQuery["order"] }) {
  return (
    <Timer
      order={order}
      isGeneralAdmissionEvent={
        order?.event?.venue?.seating === VenueSeating.NonSeated ||
        order?.membership?.venue?.seating === VenueSeating.NonSeated
      }
    >
      {({ secondsRemaining, seconds }) => {
        if (seconds > TWO_MINUTES) {
          return null;
        }

        const criticalTime = seconds <= 60;

        return (
          <Text
            variant="small"
            color={criticalTime ? "error" : undefined}
            fontWeight={criticalTime ? "bold" : undefined}
            width={"100%"}
            justifySelf={"flex-start"}
            mt={"1/4"}
            d="flex"
            alignItems={"center"}
          >
            <Icon
              icon={
                <PhosphorTimer
                  size={16}
                  weight={criticalTime ? "bold" : "regular"}
                />
              }
              mr={"1/2"}
              verticalAlign={"middle"}
            />
            Time left {secondsRemaining}
          </Text>
        );
      }}
    </Timer>
  );
}

interface ElementWrapperProps {
  paymentType: keyof AvailablePaymentMethods;
  isOrderUpdating: boolean;
  showHiddenSubmit: boolean;
  hiddenSubmitClick: () => void;
  handleElementLoadError: ExpressCheckoutElementProps["onLoadError"];
  handleConfirm: (e: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
}

function ExpressCheckoutElementAndWrapper({
  paymentType,
  isOrderUpdating,
  showHiddenSubmit,
  hiddenSubmitClick,
  handleElementLoadError,
  handleConfirm,
}: ElementWrapperProps) {
  const [elementLoading, setElementLoading] = useState(true);

  return (
    <>
      {/* Set box height to 48px which is the same as the express checkout button to prevent layout jank */}
      <Box
        height="48px"
        position={"relative"}
        opacity={isOrderUpdating ? 0.5 : 1}
        pointerEvents={isOrderUpdating ? "none" : "auto"}
        transition={"opacity 0.2s ease"}
        css={`
          #express-checkout-element {
            transition: none !important;
            margin: 0 !important;

            /* Target the iframe container */
            & > div {
              margin: 0 !important;
            }

            /* Target any nested iframes */
            iframe {
              margin: 0 !important;
            }
          }
        `}
      >
        {elementLoading && <Skeleton height="48px" width="100%" />}
        {showHiddenSubmit && (
          <Box
            position={"absolute"}
            top={0}
            right={0}
            left={0}
            bottom={0}
            bg="transparent"
            cursor="pointer"
            zIndex={1000}
            onClick={(e: FormEvent<HTMLDivElement>) => {
              e.stopPropagation();
              hiddenSubmitClick?.();
            }}
          />
        )}
        <ExpressCheckoutElement
          onReady={(e) => {
            checkoutDebug("Stripe checkout ready", e);
            setElementLoading(false);
          }}
          onConfirm={handleConfirm}
          onLoadError={handleElementLoadError}
          options={{
            billingAddressRequired: true,
            phoneNumberRequired: true,
            emailRequired: true,
            paymentMethods: {
              applePay: "never",
              googlePay: "never",
              link: "never",
              amazonPay: "never",
              klarna: "never",
              paypal: "never",

              // This will tell Stripe to only show the seleced payment method
              [paymentType]: "auto",
            },
            buttonHeight: 48,
          }}
        />
      </Box>
    </>
  );
}

function AnimateFadeIn({ children }: { children: React.ReactNode }) {
  return (
    <AnimatePresence initial={true}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        transition={{
          delay: 0.3,
          duration: 0.3,
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
